/*       */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import { connect } from 'react-redux';

import { CardContent, Divider, Box, CardHeader, Card, Chip, Tooltip, CardMedia, Checkbox, Avatar } from '@mic3/platform-ui';
import { bind, memoize } from 'app/utils/decorators/decoratorUtils';
import history from 'store/History';
import { getAttachmentUrl } from 'app/utils/attachments/attachmentsUtils';
import ResizableListItem from 'app/components/molecules/VirtualList/ResizableListItem';
import Link from 'app/components/atoms/Link/Link';
import CardIconButton from 'app/components/molecules/CardIconButton/CardIconButton';
import { showToastr } from 'store/actions/app/appActions';
import { baseRoutes } from 'app/config/routesConfig';
import { EntityLinkStyledSubTitle, LinkIcon, mobileClamp, desktopEllipsis } from 'app/containers/Entities/EntitiesList/EntitiesListItem';
import Icon from 'app/components/atoms/Icon/Icon';
import TypeIcon from 'app/components/atoms/TypeIcon/TypeIcon';
import { copyToClipboard } from 'app/utils/classification/classificationUtils';
import { getEntityUrl } from 'app/utils/entity/entityUtils';
import { toUniqueUUID } from 'app/utils/string/string-utils';

const LinkStyled = styled(Link)`
    cursor: pointer;
    text-decoration: none;
    color: ${({ theme }) => theme.material.palette.text.primary} !important;
`;

export const CardContainer = styled.div`
    width: 98%;
    max-width: 1000px;
    margin: 0 auto;
    & > .MuiCard-root {
        ${({ priority, theme }) => (priority ? `border-left: 4px solid ${theme.priorityGradients[priority][0]}` : '')};
    }
`;

export const CardStyled = styled(Card)`
    height: auto;
    cursor: pointer;

    &:hover {
        background: ${({ theme, selected }) => theme.material.colors.background.hover};
    }
    ${({ theme, selected }) => selected && `background-color: ${theme.material.colors.itemActive} !important;`}
`;

export const CardDivider = styled(Divider)`
    && {
        margin: 0 12px;
        height: 16px;
        position: relative;
        top: 3px;
    }
    @media (max-width: 481px) {
        && {
            height: auto;
            top: 0;
            margin: 0 8px;
            align-self: stretch;
        }
    }
`;

const CheckboxWrapper = styled.div`
    .MuiFormControlLabel-root {
        margin-right: 0 !important;
        margin-left: 0 !important;
    }
    .MuiCheckbox-root {
        padding: 0 !important;
        padding-bottom: 3px !important;
    }
`;

const IconOrCheckboxWrapper = styled.div`
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const AvatarIconWrapper = styled.div`
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px !important; 
`;

const TitleLinkStyled = styled(Link)`
   && {
        cursor: pointer;
    }
    font-size: 14px;
    text-decoration: none;
    &:hover {
        text-decoration: underline;
    }
    color: ${({ theme }) => theme.material.palette.text.primary} !important;
    
    ${({ isMobile }) => (isMobile ? mobileClamp : desktopEllipsis)}
`;



export const CardHeaderStyled = styled(CardHeader)`
    position: relative;
    flex: 1;
    padding-right: 190px !important;

    & .MuiCardHeader-title {
        display: flex;
        align-items: center;
    }
    & .MuiCardHeader-action {
        position: absolute;
        right: 10px;
        top: 10px;
    }
    & .MuiCardHeader-content {
        width: 1px;
    }
    & .MuiCardHeader-subheader {
        display: flex;
    }

    @media (max-width: 481px) {
        padding-right: 50px !important;
    }
`;

export const CardImage = styled(CardMedia)`
    && {
        width: 180px;
        height: 180px;
        background-size: containe !important;
        background-repeat: no-repeat !important;
        margin-right: 12px;
        &.mobile {
            width: 100%;
            height: 100px;
        }
    }
`;

export const CardContentStyled = styled(CardContent)`
    flex-grow: 1;
    height: 100%;

    && {
        padding-top: 10px;
        padding-bottom: 10px;
    }
`;

export const CardDividerTitle = styled(Divider)`
    margin: 0 8px 8px !important;
`;

/**
 *
 */
class EntityListAttributeModalItem extends PureComponent {
    /**
     * Define our prop-types for Maps card
     */
    static propTypes = {
        data: PropTypes.object,
        index: PropTypes.number,
        resetView: PropTypes.func,
        forceUpdateGrid: PropTypes.func,
        profile: PropTypes.object,
        onClick: PropTypes.func,
    };

    constructor(props) {
        super(props);

        this.inputField = React.createRef();
        this.state = {
            isHovered: false,
        };
    }

    @bind
    goToClassification(id) {
        return () => history.push(`/classifications/${id}`);
    }

    @bind
    @memoize()
    buildClasses(classes) {
        return classes.map(({ name, id }) => <Chip key={name} variant="default" label={name} size="small" onClick={this.goToClassification(id)} />);
    }

    @bind
    renderIcon(id, permissions) {
        const { canComment } = permissions || {};
        let iconButtons = [
            {
                label: 'Layers',
                name: 'layers',
                type: 'mdi',
            },
            {
                label: 'About',
                name: 'information-outline',
                type: 'mdi',
            },
            {
                label: 'A-Live',
                name: 'messenger',
                type: 'af',
            },
            {
                label: 'Sharing',
                name: 'share-variant',
                type: 'mdi',
            },
        ];

        if (!canComment) {
            iconButtons = iconButtons.filter(({ label }) => label !== 'A-Live');
        }

        return (
            <Box display="flex">
                {iconButtons.map(({ label, name, type, value, count }, index) => {
                    return (
                        <Tooltip title={label} key={`${index}-${value}`}>
                            <LinkStyled
                                to={{
                                    pathname: `${baseRoutes.map}/${id}`,
                                    state: {
                                        title: label,
                                    },
                                }}
                            >
                                <CardIconButton
                                    edge={index === 0 ? 'start' : false}
                                    count={count}
                                    ref={this.inputField}
                                    iconProps={{
                                        name: name,
                                        type: type,
                                    }}
                                />
                            </LinkStyled>
                        </Tooltip>
                    );
                })}
            </Box>
        );
    }

    @bind
    copyLinkToClipBoard(e, id, type) {
        e.stopPropagation();
        copyToClipboard(
            `${window.location.origin}/#${getEntityUrl(id, type)}`
        )
            .then(() => {
                this.props.showToastr({ severity: 'success', detail: 'Link copied to clipboard' });
            })
            .catch(() => {
                this.props.showToastr({ severity: 'error', detail: 'Link could not copied to clipboard' });
            });
    }
    @bind
    copyIdToClipBoard(e,id) {
        e.stopPropagation();
        copyToClipboard(id)
            .then(() => {
                this.props.showToastr({ severity: 'success', detail: 'Task ID copied to clipboard' });
            })
            .catch(() => {
                this.props.showToastr({ severity: 'error', detail: 'Task ID could not copied to clipboard' });
            });
    }

    @bind
    @memoize()
    renderTypeIcon(type) {
        const { entityType } = this.props;
        return <TypeIcon type={entityType ? 'entityType' : type} />;
    }

    @bind
    renderImg(){
        const { data, entityType } = this.props
        const {image, id, type, iconName, iconColor, iconType } = data
        const avatarImg = image ? getAttachmentUrl(id, type, image) : null;

        if(avatarImg){
            return  (
                <AvatarIconWrapper>
                    <Avatar src={avatarImg} variant="square" />
                </AvatarIconWrapper>
            )
        }else if (iconName){
            return (
                <AvatarIconWrapper>
                    <Icon type={iconType} name={iconName} size="lg" hexColor={iconColor} />
                </AvatarIconWrapper>
            );
        } else{
            return (
                <AvatarIconWrapper>
                    <TypeIcon type={entityType ? 'entityType' : type} size="lg" color={iconColor}/>
                </AvatarIconWrapper>
            );
        }
        
    } 
    @bind
    EntityTitle({ type, data, isMobile }) {
        const { id, name } = data || {};
        if (!id || !type) {
            return null;
        }
        return <TitleLinkStyled to={`${getEntityUrl(id, type)}`} isMobile={isMobile}>{name || 'No Name'}</TitleLinkStyled>;
    };


    render() {
        const { data, style, index, resize, children, selected, onClick, attributes, isMobile } = this.props;
        if (!data) {
            return null;
        }
        const { type, id } = data || {};
        return (
            <ResizableListItem style={style} key={index} index={index} resize={resize} padding={15}>
                {resizeRow => (
                    <CardContainer>
                        <CardStyled 
                            selected={selected}
                            onClick={onClick}
                            onMouseEnter={() => this.setState({ isHovered: true })}
                            onMouseLeave={() => this.setState({ isHovered: false })}
                        >
                            <Box display="flex" flexGrow={1} flexDirection={isMobile ? 'column' : 'row'}>
                                <Box flexGrow={1}>
                                    <CardHeaderStyled 
                                        avatar={
                                            <IconOrCheckboxWrapper>
                                                {(this.state.isHovered || selected) ?
                                                    <CheckboxWrapper>
                                                        <Tooltip title={!selected ? 'Select' : 'Deselect'} >
                                                            <Checkbox  value={selected}/>
                                                        </Tooltip>
                                                    </CheckboxWrapper> 
                                                    :
                                                    this.renderImg()
                                                }
                                            </IconOrCheckboxWrapper>
                                        }
                                        title={(
                                            <>
                                                {this.renderTypeIcon(type)}
                                                <CardDivider flexItem orientation="vertical" />
                                                {this.EntityTitle({ data, type, isMobile })}
                                            </>
                                        )} 
                                        subheader={(<>
                                            <EntityLinkStyledSubTitle
                                                color="secondary"
                                                variant="caption"
                                                noWrap
                                                title="Copy ID to clipboard"
                                                onClick={e => this.copyIdToClipBoard(e, id)}
                                            >
                                                {`#${toUniqueUUID(id)}`}
                                            </EntityLinkStyledSubTitle>
                                            <LinkIcon
                                                title="Copy link to clipboard"
                                                name="link"
                                                size="sm"
                                                onClick={e =>
                                                    this.copyLinkToClipBoard(e, id, type)
                                                }
                                            />                                        
                                        </>)} 
                                        action={
                                            <Box style={{marginRight: '8px'}}>
                                                {children} 
                                            </Box>
                                        }
                                    />
                                    <CardDividerTitle />
                                    <CardContentStyled>
                                        {attributes}
                                    </CardContentStyled>
                                </Box>
                            </Box>
                        </CardStyled>
                    </CardContainer>
                )}
            </ResizableListItem>
        );
    }
}

export default connect(    
    state => ({
        isMobile: state.global.isMobile,
    }),{
        showToastr,
    })(withTheme(EntityListAttributeModalItem));
